import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  Self,
  TrackByFunction,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import {
  InputSelectField,
  InputSelectFieldOptions,
} from '../../models/form-control-field.models';

@Component({
  selector: 'pdv-input-select',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './input-select.component.html',
  styleUrls: ['./input-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputSelectComponent<TValue = unknown> implements ControlValueAccessor {
  @Input() config!: InputSelectField;
  @Input() options: InputSelectFieldOptions<TValue>[] = [];
  @Input() searchControl?: FormControl<string | null>;
  @Input() searchPlaceholder = 'Pesquisar';
  @Input() noEntriesFoundLabel = 'Nenhuma opção encontrada';
  @Input() compact = false;
  @Input() showTitle = true;
  @Output() selectionChange = new EventEmitter<TValue>();

  readonly trackByOption: TrackByFunction<InputSelectFieldOptions<TValue>> = (
    index,
    { value },
  ) => {
    return value !== null && typeof value === 'object'
      ? index
      : (value ?? index);
  };

  onTouched: () => void = () => undefined;

  constructor(@Self() public readonly ngControl: NgControl) {
    this.ngControl.valueAccessor = this;
  }

  writeValue(): void {}

  registerOnChange(): void {}

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {}

  get hasValue(): boolean {
    return this.control.value !== null && this.control.value !== undefined && this.control.value !== '';
  }

  clear(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.control.disabled) return;

    const value = this.config.clearValue ?? null;
    this.control.setValue(value);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.onTouched();
    this.selectionChange.emit(value as TValue);
  }

  get control(): FormControl {
    return this.ngControl.control as FormControl;
  }
}
